import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {delayTimeout} from '@shared/helpers/function'
import {isArray} from '@shared/helpers/lang'

import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {getPayLaterInfo} from '@web/actions/payLater'
import {setMaximumRechargePoint, updatePaymentMethod} from '@web/actions/paymentMethod'
import {enablePayLaterPointByAllPoint, updatePointPayment, usingAllNaverPoint} from '@web/actions/pointPayment'
import {ShoppingLiveData} from '@web/actions/shoppingLive'
import {EntryType, ErrorType, PayMethodType} from '@web/core/constants'
import {decodeUrl} from '@web/helpers/path'
import {isMobileDisplay} from '@web/helpers/userAgent'
import {paymentMethodInfoSelector, rechargePaymentInfoSelector} from '@web/selectors/paymentMethodInfo'
import {naverPayPointInfoSelector, payLaterInfoSelector} from '@web/selectors/pointPaymentInfo'
import {discountInfoSelector} from '@web/selectors/productInfo'
import {addNaverId} from '@web/slices/abTest/slice'
import {recalculateAction} from '@web/slices/orderTotal/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {getAvailablePayMethodTypes} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'
import {locationSelector} from '@web/slices/router'

import api from '../apis'
import {SellerOrderSheetResponse} from '../apis/response'
import {SellerOrderSheetAPIError} from '../apis/response/error'
import {CheckoutThunkApi as ThunkApi} from '../interfaces'
import {getBackUrl} from '../utils/urlHelper'

export type InitOrderSheetByAPIPayloadType = {
    data: SellerOrderSheetResponse
    /**
     * 그 외 convert 해 줄 값들은 아래에 별도 interface로 추가
     */
    /** 해외 배송 여부 (배송, 동의에 영향) */
    isOverseasDelivery: boolean
    /** 돌아가야하는 주소 */
    backUrl: string
    availablePayMethodTypes: PayMethodType[]
}

export const initOrderSheetByAPI = createAsyncThunk<
    InitOrderSheetByAPIPayloadType,
    string,
    ThunkApi<SellerOrderSheetAPIError>
>('initOrderSheetByAPI', async (orderSheetId, {getState, rejectWithValue}) => {
    const state = getState()
    const {query} = locationSelector(state)

    try {
        const {backUrl: encodeBackUrl} = query ?? {}
        const backUrl = decodeUrl(encodeBackUrl)
        const response = await api.getSellerOrderSheet({
            orderSheetId,
            backUrl,
        })

        if ('code' in response) {
            // 선물하기의 경우 reject 으로 auth 에서 처리
            return rejectWithValue({
                title: response.title,
                status: response.status,
                detail: response.detail,
                code: response.code,
                giftFailure: {
                    orderSheetId: response.orderSheetId,
                    availableGiftingPeopleCount: response.availableGiftingPeopleCount ?? response.multiSelectLimit ?? 1,
                    friendsSessionKey: response.friendsSessionKey,
                },
                fallbackMessage: '',
                validationFailure: {
                    message: '',
                    fallbackMessage: '',
                    reasonType: '',
                    reasonAttributes: null,
                },
            })
        }

        type DataType = InitOrderSheetByAPIPayloadType['data']

        const result: InitOrderSheetByAPIPayloadType = {
            data: {
                ...response,
                products: response.products,
                deliveryAddressBook: response.deliveryAddressBook,
                payMethod: response.payMethod,
                naverpayPoint: response.naverpayPoint,
                coupon: response.coupon as DataType['coupon'],
                couponFromMyOptimalDiscount: response.couponFromMyOptimalDiscount,
                branchDeposit: response.branchDeposit,
                cardPromotionV2: response.cardPromotionV2,
                cardPromotionV2FromMyOptimalDiscount: response.cardPromotionV2FromMyOptimalDiscount,
                shoppingLiveConfigInfo: response.shoppingLiveConfigInfo,
            },
            isOverseasDelivery: response.products.some(({overseasDelivery}) => overseasDelivery),
            backUrl: getBackUrl({
                backUrl,
                isPC: !isMobileDisplay,
                sellerRegisterType: response.sellerRegisterType,
                channel: response.saleChannelType,
                productUrl: response.products[0]?.productUrl ?? '',
            }),
            availablePayMethodTypes: getAvailablePayMethodTypes({
                availablePayMethodTypes: response.payMethod.availablePayMethodTypes,
                pointRestrictPolicy: 'EACH',
            }),
        }

        return result
    } catch (e) {
        if (isApiError(e)) {
            if (e.response?.status === 504) {
                return rejectWithValue({
                    code: ErrorType.ETIMEDOUT_FAILED,
                    fallbackMessage: '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
                    status: e.response.status,
                } as SellerOrderSheetAPIError)
            }
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/** 주문서 초기 설정 action */
export const initOrderSheet = createAsyncThunk<
    {
        isApiSuccess: boolean
        isRestore: boolean
        isZipCodeChanged: boolean
        channel: Maybe<EntryType>
        shoppingData: Maybe<ShoppingLiveData>
    },
    string,
    ThunkApi
>('initOrderSheet', async (orderSheetId, {dispatch, getState}) => {
    const {data} = await dispatch(initOrderSheetByAPI(orderSheetId)).unwrap()
    // 데이터가 없다면 주문서 조회 실패
    const isApiSuccess = !!data
    let isRestore = false
    let isZipCodeChanged = false

    if (isApiSuccess) {
        /** 총 할인 금액 */
        const discountInfo = discountInfoSelector(getState())
        dispatch(
            recalculateAction({
                totalDiscountAmount: discountInfo.totalDiscountAmount,
                productDiscountAmount: discountInfo.totalProductDiscountAmount,
                deliveryDiscountAmount: discountInfo.totalDeliveryDiscountAmount,
            }),
        )

        // 초기 결제수단으로 후불결제 선택이 필요하기 때문에 회원정보 조회
        await dispatch(getPayLaterInfo())

        if (payLaterSelector(getState()).enabled) {
            // 데이터 반영후 후불결제 최대화 계산
            await dispatch(enablePayLaterPointByAllPoint())
        } else if (naverPayPointSelector(getState()).usePointAll) {
            // 데이터 반영후 포인트 최대화 계산
            await dispatch(usingAllNaverPoint())
        } else if (rechargePaymentInfoSelector(getState()).isNeedRecharging) {
            // 데이터 반영후 충전포인트 최대화 계산
            await dispatch(setMaximumRechargePoint())
        }
        // 화면이 노출되고 confirm 가 노출되어야 합니다.
        await delayTimeout(0)

        await dispatch(initOrderSheetByStorage({orderSheetId, isCouponFailed: data.coupon.fallback}))
            .unwrap()
            .then(({data: restoreData, differences, isRestore: shouldRestore}) => {
                isRestore = shouldRestore
                if (restoreData && differences && isArray(differences)) {
                    isZipCodeChanged = differences.includes('DELIVERY')
                }
            })
            .catch((reject) => {
                isZipCodeChanged = reject && reject.case === 'FIRST_DELIVERY_PASS'
            })

        // 포인트 사용시 업데이트 필요 여부
        if (naverPayPointInfoSelector(getState()).isNeedUpdate || payLaterInfoSelector(getState()).isNeedUpdate) {
            await dispatch(updatePointPayment())
        } else if (rechargePaymentInfoSelector(getState()).isNeedRecharging) {
            // 충전결제 복원시 사용이 0원 이라면, 최대 적용으로 변경 합니다.
            await dispatch(setMaximumRechargePoint())
        }
        // 복원후 복합결제 상황이라면 결제수단 선택
        if (paymentMethodInfoSelector(getState()).isNeedSelectedPayMethod) {
            await dispatch(updatePaymentMethod())
        }
    }

    dispatch(addNaverId(data.purchaser.naverId))
    return {
        isApiSuccess,
        isRestore,
        isZipCodeChanged,
        channel: (data?.saleChannelType ?? null) as Maybe<EntryType>,
        shoppingData: (data?.shoppingLiveConfigInfo ?? null) as Maybe<ShoppingLiveData>,
    }
})
